import React from "react";
import type { New } from "../../../../api/client/types/new.types";
import type { PaginatedResponse } from "../../../../api/client/types/common.types";
import { PaginatedTable } from "../../../../shared/components/ui/PaginatedTable";
import { type ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { ROUTES } from "../../../../routes/routes";
import { Eye } from "lucide-react";

interface NewTableProps {
  news: PaginatedResponse<New>;
  isLoading?: boolean;
  isError?: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export const NewTable: React.FC<NewTableProps> = ({
  news,
  isLoading,
  isError,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const columns = React.useMemo<ColumnDef<New>[]>(
    () => [
      {
        id: "actions",
        header: "Acciones",
        cell: ({ row }) => {
          const to = `${ROUTES.NEWS.NEW_DETAIL(row.original.id)}`;
          return (
            <div className="flex items-center gap-1">
              <Link
                to={to}
                title="Ver"
                className="text-gray-500 hover:text-blue-600 transition-colors cursor-pointer"
              >
                <Eye size={18} />
              </Link>
            </div>
          );
        },
        enableSorting: false,
      },
      {
        header: "Tipo de reporte",
        accessorKey: "report_type.name",
        cell: ({ row }) => row.original.report_type?.name || "—",
      },
      {
        header: "W oficial",
        accessorKey: "security_officer_name",
        cell: ({ row }) => row.original.security_officer_name || "N/A",
      },
      {
        header: "Fecha del evento",
        accessorKey: "event_timestamp",
        cell: ({ getValue }) => {
          const date = new Date(getValue() as string);
          return new Intl.DateTimeFormat("es-CO", {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }).format(date);
        },
      },
      {
        header: "Creado el",
        accessorKey: "created_at",
        cell: ({ getValue }) => {
          const date = new Date(getValue() as string);
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        },
      },
    ],
    []
  );

  return (
    <PaginatedTable<New>
      data={news}
      columns={columns}
      isLoading={isLoading}
      isError={isError}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      totalLabel="Total"
      entityName="novedades"
    />
  );
};
